import { chain } from 'lodash';
import moment from 'moment';

import * as dto from 'client/dto';
import { AggregationCat } from 'models/DiseaseModels';
import { ProximalCaseVM } from 'models/EventModels';
import { RiskLikelihood, RiskMagnitude } from 'models/RiskCategories';
import { TimeFrameEnumType } from 'models/TimeframeModels';
import { locationTypePrint } from 'utils/stringFormattingHelpers';

import { DADateRangeType } from 'components/_graphs/_common/DiseaseActivityModels';

import { hasIntersection, mapToNumericDictionary } from './arrayHelpers';
import { formatDate, getMomentAgo } from './dateTimeHelpers';
import mapHelper from './mapHelper';

export const map2RiskLikelihood = (
  minVal: number,
  maxVal: number,
  isModelNotRun,
  isModelRunning
): RiskLikelihood => {
  const avg = (minVal + maxVal) / 2;
  return isModelNotRun
    ? 'Not calculated'
    : isModelRunning
    ? 'Calculating, revisit later!'
    : avg < 0.01
    ? 'Unlikely'
    : avg <= 0.1
    ? 'Low'
    : avg <= 0.5
    ? 'Moderate'
    : avg <= 0.9
    ? 'High'
    : avg <= 1
    ? 'Very high'
    : 'Not calculated';
};

export const getRiskLikelihood = (risk: dto.RiskModel): RiskLikelihood =>
  risk
    ? map2RiskLikelihood(
        risk.minProbability,
        risk.maxProbability,
        risk.isModelNotRun,
        risk.isModelRunning
      )
    : 'Not calculated';

export const getRiskLikelihoodAvg = (risk: dto.RiskModel) =>
  ((risk?.minProbability || 0) + (risk?.maxProbability || 0)) / 2;

export const map2RiskMagnitude = (
  minVal: number,
  maxVal: number,
  isModelNotRun,
  isModelRunning
): RiskMagnitude => {
  const avg = (minVal + maxVal) / 2;
  return isModelNotRun
    ? 'Not calculated'
    : isModelRunning
    ? 'Calculating, revisit later!'
    : minVal === 0 && maxVal === 0
    ? 'Negligible'
    : avg <= 10
    ? 'Up to 10 cases'
    : avg <= 100
    ? '11 to 100 cases'
    : avg <= 1000
    ? '101 to 1,000 cases'
    : avg > 1000
    ? '>1,000 cases'
    : 'Not calculated';
};

export const getRiskMagnitude = (risk: dto.RiskModel): RiskMagnitude =>
  risk
    ? map2RiskMagnitude(
        risk.minMagnitude,
        risk.maxMagnitude,
        risk.isModelNotRun,
        risk.isModelRunning
      )
    : 'Not calculated';

export const MapProximalLocations2VM = (
  proximalLocations: dto.ProximalCaseCountModel[]
): ProximalCaseVM => {
  if (!proximalLocations) return null;
  return {
    dictByLocationId: mapToNumericDictionary(
      proximalLocations,
      x => x.locationId,
      x => x
    ),
    geonameIds: proximalLocations.map(e => e.locationId),
    totalCasesIn: proximalLocations.reduce(
      (acc, x) => acc + (x.isWithinLocation ? x.proximalCases.reportedCases : 0),
      0
    ),
    totalCasesNear: proximalLocations.reduce(
      (acc, x) => acc + (!x.isWithinLocation ? x.proximalCases.reportedCases : 0),
      0
    ),
    totalCases: proximalLocations.reduce((acc, x) => acc + x.proximalCases.reportedCases, 0),
    casesCityLevel: chain(proximalLocations)
      .filter(x => x.locationType === dto.LocationType.City)
      .orderBy([x => (x.isWithinLocation ? 0 : 1), x => x.locationName])
      .value(),
    casesProvinceCountryLevel: chain(proximalLocations)
      .filter(x => mapHelper.isPolygon(x.locationType))
      .map(x => ({
        ...x,
        locationTypeSubtitle: locationTypePrint(x.locationType),
      }))
      .orderBy([x => (x.isWithinLocation ? 0 : 1), x => x.locationType, x => x.locationName])
      .value(),
  };
};

export function GetStartDateOrNullIfAll(rangeType: DADateRangeType): Date {
  return rangeType === '90-days'
    ? getMomentAgo('last-90-days').toDate()
    : rangeType === '1-year'
    ? getMomentAgo('last-year').toDate()
    : null;
}

export type DaAggregationThresholdConfig = { threeday: number; week: number; month: number };
export function ComputeAggregationCategory(options: {
  startDate: Date;
  endDate: Date;
  thresholds: DaAggregationThresholdConfig;
}): AggregationCat {
  const { startDate, endDate, thresholds } = options;
  const nDays = moment(endDate).diff(moment(startDate), 'days');
  return nDays <= thresholds.threeday
    ? 'day'
    : nDays <= thresholds.week
    ? '3-days'
    : nDays <= thresholds.month
    ? 'week'
    : 'month';
  // NOTE: 05f34727: year not used so far
}

export const MapAggregationCat2Request: {
  [key in AggregationCat]: dto.TemporalCaseCountRequestModel;
} = {
  day: {
    aggregationCategory: dto.TemporalAggregationCategory.Day,
  },
  '3-days': {
    aggregationCategory: dto.TemporalAggregationCategory.Day,
    aggregationTime: 3,
  },
  week: {
    aggregationCategory: dto.TemporalAggregationCategory.Week,
  },
  month: {
    aggregationCategory: dto.TemporalAggregationCategory.Month,
  },
  year: {
    aggregationCategory: dto.TemporalAggregationCategory.Year,
  },
};

export const MapAggregationCat2AggregationDays: {
  [key in AggregationCat]: number;
} = {
  day: 1,
  '3-days': 3,
  week: 7,
  month: 30,
  year: 365,
};

export const MapAggregationCat2TooltipLabel: {
  [key in AggregationCat]: string;
} = {
  day: '1 day',
  '3-days': '3 days',
  week: '1 week',
  month: '1 month',
  year: '1 year',
};

export const MapAggregationCat2LegendLabel: {
  [key in AggregationCat]: string;
} = {
  day: '7 day avg.',
  '3-days': '21 day avg.',
  week: '7 week avg.',
  month: '7 month avg.',
  year: '7 year avg.',
};

export const timeframeDescription = (timeframe: TimeFrameEnumType, startDate: string) => {
  switch (timeframe) {
    case '30days':
      return 'in the last 30 days';
    case '60days':
      return 'in the last 60 days';
    default:
      return `since ${formatDate(startDate)}`;
  }
};

export const simpleTimeFrameDescription = (timeFrame: TimeFrameEnumType, startDate: string) => {
  switch (timeFrame) {
    case '30days':
      return 'Last 30 days';
    case '60days':
      return 'Last 60 days';
    case '1year':
      return 'Last 1 year';
    default:
      return formatDate(startDate);
  }
};

export type RiskLikelihoodPopupText = {
  title: string;
  message: string;
};
export const RiskLikelihoodPopupTextMapping: Partial<{
  [key in RiskLikelihood]: RiskLikelihoodPopupText;
}> = {
  'Not calculated': {
    title: null,
    message: 'Not calculated',
  },
  'Calculating, revisit later!': {
    title: null,
    message: 'Calculating, revisit later!',
  },
  Unlikely: {
    title: 'Unlikely',
    message: '<1%',
  },
  Low: {
    title: 'Low Likelihood',
    message: '1% to 10%',
  },
  Moderate: {
    title: 'Moderate Likelihood',
    message: '11% to 50%',
  },
  High: {
    title: 'High Likelihood',
    message: '51% to 90%',
  },
  'Very high': {
    title: 'Very High Likelihood',
    message: '91% to 100%',
  },
};

export function IsLocationProximalToUserLocations(
  geoTree: {
    locationId?: number;
    provinceId?: number;
    countryId?: number;
  },
  userLocationIds: number[]
) {
  return hasIntersection(userLocationIds || [], [
    geoTree.locationId,
    ...(geoTree.provinceId ? [geoTree.provinceId] : []),
    ...(!!geoTree.countryId && geoTree.countryId !== -1 ? [geoTree.countryId] : []),
  ]);
}
